'use client';

import { useEffect, useMemo } from 'react';

import { useInfiniteQuery, UseQueryResult } from '@tanstack/react-query';

import { useBaseAuthenticated } from 'shared/base/contexts/baseAuth';

import {
  getPlatformExecuteAdHocQueryTeamQueryKey,
  platformExecuteAdHocQueryTeam,
} from '../api/platform';
import {
  PlatformExecuteAdHocQueryTeam200,
  PlatformExecuteAdHocQueryTeamParams,
} from '../api/platform/models';

type QueryInput = {
  baseQuery: string;
  params: PlatformExecuteAdHocQueryTeamParams & { max_pages?: number };
};

export function useFetchAllQueries(
  query: QueryInput,
): UseQueryResult<PlatformExecuteAdHocQueryTeam200, unknown> {
  const { currentMembership } = useBaseAuthenticated();

  if (!query.baseQuery) {
    throw new Error('You must specify a query');
  }

  const perPage = query.params?.per_page || 1000;
  const maxPages = query.params?.max_pages || 50;

  const infiniteQuery = useInfiniteQuery({
    queryFn: ({ pageParam = 1 }) => {
      return platformExecuteAdHocQueryTeam(
        currentMembership.team.name ?? '',
        { query: query.baseQuery },
        { ...query.params, page: pageParam, per_page: perPage },
      );
    },
    queryKey: getPlatformExecuteAdHocQueryTeamQueryKey(
      currentMembership.team.name ?? '',
      { query: query.baseQuery },
      query.params,
    ),
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    getNextPageParam: (lastPage, pages) => {
      const responseData = lastPage.data;

      if (pages.length >= maxPages) {
        return undefined;
      }

      // Check if there are more pages based on last_page
      if (responseData?.metadata?.last_page && pages.length < responseData.metadata.last_page) {
        return pages.length + 1;
      }

      return undefined;
    },
  });

  useEffect(() => {
    if (
      infiniteQuery.status === 'success' &&
      infiniteQuery.hasNextPage &&
      !infiniteQuery.isFetchingNextPage
    ) {
      infiniteQuery.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    infiniteQuery.status,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  const result = useMemo(() => {
    const processedData = infiniteQuery.data?.pages?.[0]?.data
      ? {
          data: {
            columns: infiniteQuery.data.pages[0].data.data.columns,
            query: infiniteQuery.data.pages[0].data.data.query,
            rows: (() => {
              const allRows: any[] = [];
              const { pages } = infiniteQuery.data;

              for (const page of pages) {
                if (page.data.data.rows) {
                  allRows.push(...page.data.data.rows);
                }
              }

              return allRows;
            })(),
          },

          metadata: infiniteQuery.data.pages[0].data.metadata,
        }
      : undefined;

    const hasMoreData = infiniteQuery.hasNextPage || infiniteQuery.isFetchingNextPage;
    const allPagesLoaded = infiniteQuery.status === 'success' && !hasMoreData;

    return {
      data: processedData,
      dataUpdatedAt: infiniteQuery.dataUpdatedAt,
      error: infiniteQuery.error,
      errorUpdatedAt: infiniteQuery.errorUpdatedAt,
      fetchStatus: hasMoreData ? 'fetching' : infiniteQuery.fetchStatus,
      isError: infiniteQuery.isError,
      isFetched: allPagesLoaded && infiniteQuery.isFetched,
      isFetchedAfterMount: allPagesLoaded && infiniteQuery.isFetchedAfterMount,
      isFetching: hasMoreData || infiniteQuery.isFetching,
      isLoading: !allPagesLoaded || infiniteQuery.isLoading,
      isLoadingError: false,
      isPlaceholderData: false,
      isPreviousData: false,
      isRefetchError: false,
      isStale: infiniteQuery.isStale,
      isSuccess: allPagesLoaded,
      refetch: infiniteQuery.refetch,
      remove: infiniteQuery.remove,
      status: allPagesLoaded ? 'success' : 'loading',
    } as UseQueryResult<PlatformExecuteAdHocQueryTeam200, unknown>;
  }, [infiniteQuery]);

  return result;
}

/**
 * Custom hook to fetch all pages of data for multiple queries
 * @param queries Array of dashboard card queries to fetch
 * @returns Array of query results with all pages fetched
 */
export function useFetchAllParallelQueries(
  queries: QueryInput[],
): UseQueryResult<PlatformExecuteAdHocQueryTeam200, unknown>[] {
  if (!queries || queries.length === 0) {
    throw new Error('You must specify queries');
  }

  return queries.map((query) => {
    // Using a workaround with eslint disable to enable multiple hook calls
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useFetchAllQueries(query);
  });
}
